import React from 'react';
import useMedicalProfile from '../../../../../../hooks/api/useMedicalProfile';
import ProfileCardButtons from '../ProfileCardButtons';
import { getIntl } from '../../../../../../utils/intl';
import { ActionButtonLayout } from '../../../../../v2BaseComponents/ActionButtonLayout';
import { useIsProfileOwner } from '../../../../../v2BaseComponents/Permissions/hooks';
import useOneUpHealthRefresh from '../../../../../../hooks/api/useOneUpRefresh';
import useProfileTransferRequest from '../../../../../../hooks/api/useProfileTransferRequest';
import { useProfilesTabNavigation } from '../../../../../../utils/appNavigationNames';
import { useDeleteProfileButton } from '../../../../../../hooks/useDeleteButtonModal';
import { useRemoveSelfAsCollaboratorButton } from '../../../../../../hooks/useRemoveSelfAsCollaboratorButton';
import MainContentContainer, { mainContentPadding } from '../../../../../v2BaseComponents/MainContentContainer';
import { Action } from '../../../../../v2BaseComponents/ActionButtonLayout/types';
import PendingOwnershipTransferAlert from '../PendingOwnershipTransferAlert';
import Stack, { contentStackSpacing } from '../../../../../v2BaseComponents/Stack';
import { AnimateLoadedContent } from '../../../../../v2BaseComponents/Loader';
import useInboundTransferRequests from '../../../../../../hooks/api/useInboundTransferRequests';
import SizedView from '../../../../../v2BaseComponents/SizedView';
import { DEFAULT_MAX_WIDTH } from '../../../../../v2BaseComponents/MaxWidthRestrictedContent';
import useRecordEvent from '../../../../../../hooks/useRecordEvent';
import { useLoadedUser } from '../../../../../../hooks/api/useUser';
import ProfileSubscribeBanner from './ProfileSubscribeBanner';

const intl = getIntl('profileScreenHealthTab', {
    invite: 'Invite Collaborator',
    refresh: 'Refresh Patient Portals',
    createTransferRequest: 'Transfer Owner',
    deleteProfile: 'Delete Profile',
    removeSelfAsCollaborator: 'Remove Self As Collaborator',
    transferRequestDetails: 'Transfer Request Details',
});

export type HealthTabOwnProps = {
    profileId: string;
};

export type HealthTabProps = HealthTabOwnProps;

const HealthTab: React.FC<HealthTabProps> = ({ profileId }) => {
    const navigation = useProfilesTabNavigation();
    const { user } = useLoadedUser({});
    const { isLoadingProfile } = useMedicalProfile({ medicalProfileId: profileId });
    const isProfileOwner = useIsProfileOwner(profileId);
    // `canRefreshPortals` permission lookup removed alongside the hidden
    // refresh action below (1upHealth refresh is no longer supported).
    const { isRefreshing, refresh } = useOneUpHealthRefresh();
    const { isLoadingProfileTransferRequest, profileTransferRequest } = useProfileTransferRequest({ profileId });
    const { isLoadingInboundTransferRequests } = useInboundTransferRequests();
    const recordEvent = useRecordEvent();
    const deleteProfileButton = useDeleteProfileButton({
        profileId,
        onDeleteSuccess: () => {
            recordEvent('deleteProfile');
            navigation.navigate('root');
        },
    });
    const removeSelfAsCollaboratorButton = useRemoveSelfAsCollaboratorButton({
        profileId,
        onRemoveSuccess: () => {
            navigation.navigate('root');
        },
    });

    const actions: Action[] = [
        {
            icon: 'UserPlus',
            title: intl('invite'),
            onPress: () => {
                navigation.navigate('invite-collaborator', {
                    profileId,
                });
            },
            show: isProfileOwner,
        },
        {
            icon: 'Trash2',
            title: intl('deleteProfile'),
            onPress: deleteProfileButton.onPress,
            show: isProfileOwner,
            isDisabled: deleteProfileButton.isDeletingProfile,
            displayInMoreMenu: true,
            testID: 'delete-profile-prompt-button',
        },
        {
            // 1upHealth patient portal refresh is no longer supported. The
            // refresh action is hidden here (instead of being shown as disabled)
            // because this is a profile-level action menu — surfacing a
            // permanently-disabled "Refresh Patient Portals" entry here adds
            // noise. The portal detail page still shows a disabled refresh
            // button plus an explanatory alert; see
            // app/src/components/screens/Tabs/Profiles/PortalConnectionScreen.
            icon: 'RefreshCw',
            title: intl('refresh'),
            onPress: () => refresh(profileId),
            show: false,
            isDisabled: isRefreshing,
        },
        {
            icon: 'HorizontalArrows2',
            title: intl('createTransferRequest'),
            onPress: () => {
                navigation.navigate('add-transfer-request', {
                    profileId,
                });
            },
            show:
                !isLoadingProfileTransferRequest &&
                isProfileOwner &&
                profileTransferRequest.status === 'no-pending-transfer-request',
            testID: 'create-transfer-request-button',
        },
        {
            icon: 'HorizontalArrows2',
            title: intl('transferRequestDetails'),
            onPress: () => {
                navigation.navigate('transfer-request', {
                    profileId,
                });
            },
            show:
                !isLoadingProfileTransferRequest &&
                isProfileOwner &&
                (profileTransferRequest.status === 'pending-collaborator-transfer-request' ||
                    profileTransferRequest.status === 'pending-non-collaborator-transfer-request'),
            testID: 'transfer-request-details-button',
        },
        {
            icon: 'UserMinus',
            title: intl('removeSelfAsCollaborator'),
            onPress: removeSelfAsCollaboratorButton.onPress,
            show: !isProfileOwner && user.type !== 'professional',
            isDisabled: removeSelfAsCollaboratorButton.isRemovingSelfAsCollaborator,
            testID: 'remove-self-as-collaborator-prompt-button',
        },
    ];

    return (
        <AnimateLoadedContent
            isLoading={isLoadingProfile || isLoadingProfileTransferRequest || isLoadingInboundTransferRequests}
        >
            {() => (
                <>
                    <SizedView
                        style={{ flex: 1, alignSelf: 'stretch' }}
                        placeholderStyle={{ flex: 1, alignSelf: 'stretch' }}
                    >
                        {({ width }) => {
                            const shouldRenderInMainContentContainer = width < DEFAULT_MAX_WIDTH;
                            return (
                                <ActionButtonLayout actions={actions} scrollable>
                                    <MainContentContainer
                                        disableRenderInContainer={!shouldRenderInMainContentContainer}
                                    >
                                        <Stack
                                            vertical
                                            fullWidth
                                            space={contentStackSpacing}
                                            style={{
                                                paddingTop: !shouldRenderInMainContentContainer
                                                    ? mainContentPadding
                                                    : undefined,
                                                paddingBottom: !shouldRenderInMainContentContainer
                                                    ? mainContentPadding
                                                    : undefined,
                                            }}
                                        >
                                            <ProfileSubscribeBanner profileId={profileId} />
                                            {profileTransferRequest?.status !== 'no-pending-transfer-request' ? (
                                                <PendingOwnershipTransferAlert profileId={profileId} />
                                            ) : null}
                                            <ProfileCardButtons profileId={profileId} />
                                        </Stack>
                                    </MainContentContainer>
                                </ActionButtonLayout>
                            );
                        }}
                    </SizedView>
                    {deleteProfileButton.modal}
                    {removeSelfAsCollaboratorButton.modal}
                </>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(HealthTab);
